import { type Input } from "antd";

import { type AppDefaultInfo, type UserInfo } from "helpers/AppProvider.types";

import { BTLocalStorage } from "types/btStorage";
import { BTLoginTypes, LoginStatus } from "types/enum";

import { isNullOrUndefined } from "utilities/object/object";
import {
    getPortalForLoginType,
    getPortalTypeForUrl,
    isPortalAgnosticUrl,
    PortalType,
} from "utilities/portal/portal";
import { getBaseRoute, routes } from "utilities/routes";
import { routesWebforms } from "utilities/routesWebforms";
import { shutdownChat } from "utilities/thirdPartyIntegrations/serviceCloud/serviceCloud";
import { getOAuthUrl } from "utilities/url/url";

import { clearJobPickerKeys } from "commonComponents/utilities/JobPicker/JobPicker.utils";

import { markCredentialModalRendered } from "entity/login/credentialUpgradeState";
import {
    type ILoginResponse,
    type IRedirectPageParams,
    LoginPageRedirect,
    LoginResponse,
    type OAuthType,
} from "entity/login/Login/Login.api.types";
import { ScheduleListContainerTabs } from "entity/schedule/ScheduleListContainer/ScheduleListContainer.api.types";

export function getCanRedirectForExistingLogin(
    reason: string | undefined,
    oauthRedirectType: OAuthType | undefined,
    userInfo: UserInfo | undefined,
    promptLogin: boolean | undefined,
) {
    return (
        reason === undefined && // do not redirect when the user is presented with a reason
        !(oauthRedirectType && promptLogin) && // do not redirect during oauth flow where prompt=login
        userInfo !== undefined && // require a user
        userInfo != null &&
        userInfo.globalUserId !== null &&
        (userInfo.loginType === BTLoginTypes.BUILDER || // only these 5 login types are supported
            userInfo.loginType === BTLoginTypes.SUBS ||
            userInfo.loginType === BTLoginTypes.OWNER ||
            userInfo.loginType === BTLoginTypes.CONTACT)
    );
}

export function handleAutoFill<TValues>(
    usernameKey: keyof TValues,
    usernameValue: string,
    usernameRef: React.RefObject<Input>,
    setFieldValue: (key: keyof TValues, value: any) => void,
) {
    if (usernameRef.current && usernameRef.current.state.value !== usernameValue) {
        setFieldValue(usernameKey, usernameRef.current.state.value);
    }
}

export const getLastPageRequested = (loginType: BTLoginTypes, loginPostRedirect: string) => {
    const decodedPostLoginUrl = unescape(loginPostRedirect);
    let redirectUrl = "/".concat(decodedPostLoginUrl);
    redirectUrl = redirectUrl.replace("//", "/");

    if (
        !isPortalAgnosticUrl(loginPostRedirect) &&
        getPortalTypeForUrlIncludingNonSpa(loginPostRedirect) !== getPortalForLoginType(loginType)
    ) {
        return routes.landingPage.getLinkForLoginType(loginType, true);
    }

    return redirectUrl;
};

const getPortalTypeForUrlIncludingNonSpa = (url: string): PortalType | null => {
    const portalType = getPortalTypeForUrl(url);
    return portalType ?? getPortalTypeForWebforms(url);
};

const getPortalTypeForWebforms = (url: string): PortalType | null => {
    if (url.toLowerCase().includes("subsummary.aspx")) {
        return PortalType.SUBS;
    }
    return null;
};

export const handleRedirectPage = (params: IRedirectPageParams) => {
    const response = params.loginResponse;
    const redirectPage = getRedirectPage(response, params.isOAuthRequest, params.loginPostRedirect);

    // Clears localStorage of previous Job Picker keys
    if (response.builderId) {
        clearJobPickerKeys(response.builderId);
    }

    // Clear credential upgrade flag on successful login that doesn't require upgrade
    // This prevents stale flags from previous users/sessions from blocking this login
    if (
        response instanceof LoginResponse &&
        !response.requireEmailCredId &&
        redirectPage !== LoginPageRedirect.RequireEmail
    ) {
        BTLocalStorage.remove("bt-object-credentialUpgradeRequired");
    }

    if (!(response instanceof LoginResponse) && redirectPage !== LoginPageRedirect.OAuth) {
        return;
    }

    if (params.isOAuthRequest && (response as LoginResponse).requireEmailCredId) {
        // Mark credential upgrade flow as active BEFORE components mount
        // This prevents RedirectForCredentialUpgrade from logging out during initial login
        markCredentialModalRendered();

        params.setLoginStateForRequireEmail!(
            (response as LoginResponse).requireEmailCredId,
            (response as LoginResponse).credUpgradeEmailSuggestion,
        );
        return;
    }

    if (redirectPage === LoginPageRedirect.OAuth) {
        handleOAuthRedirect(params, response);
    } else if (response instanceof LoginResponse) {
        handleLoginResponseRedirect(params, response, redirectPage);
    }
};

export const handleLogout = (appDefaults?: AppDefaultInfo) => {
    BTLocalStorage.remove("bt-object-previousAuthStoreInfo");
    BTLocalStorage.remove("bt-number-selectedBuilderId");
    BTLocalStorage.remove("bt-object-selectedJobsiteIdWithBuilder");
    BTLocalStorage.remove("bt-object-credentialUpgradeRequired");
    if (appDefaults?.enableServiceCloudChat) {
        BTLocalStorage.removeServiceCloudStorageItems(
            appDefaults?.serviceCloudConfig.salesforceOrgId,
        );
        shutdownChat();
    }
    const logoutUrl = routes.logout.getLogoutLink();
    window.location.assign(logoutUrl);
};

const handleOAuthRedirect = (params: IRedirectPageParams, response: ILoginResponse) => {
    const oauthRedirectState = BTLocalStorage.get("bt-object-oauthRedirectState");
    BTLocalStorage.remove("bt-object-oauthRedirectState");
    const redirectURI = params.oauthRedirectUri ?? oauthRedirectState?.redirectURI;
    const stateParam = params.oauthState ?? oauthRedirectState?.state;
    if (redirectURI && stateParam && response.oAuthCode) {
        window.location.assign(getOAuthUrl(redirectURI, response.oAuthCode, stateParam));
    }
};

const handleLoginResponseRedirect = (
    params: IRedirectPageParams,
    response: LoginResponse,
    redirectPage: LoginPageRedirect,
) => {
    switch (redirectPage) {
        case LoginPageRedirect.FrozenSubOwner:
            handleFrozenSubOwnerRedirect();
            break;
        case LoginPageRedirect.FrozenBuilder:
            handleFrozenBuilderRedirect(response);
            break;
        case LoginPageRedirect.SubNoJobAccess:
            window.location.assign(getBaseRoute() + routes.bid.getListLink());
            break;
        case LoginPageRedirect.Summary:
            window.location.assign(
                routes.landingPage.getLinkForLoginType(response.loginType, true),
            );
            break;
        case LoginPageRedirect.CalendarPageOwner:
            window.location.assign(
                getBaseRoute() + routes.schedule.getListLink(ScheduleListContainerTabs.Default),
            );
            break;
        case LoginPageRedirect.PostLoginRedirect:
            handlePostLoginRedirect(params, response);
            break;
        case LoginPageRedirect.MFA:
            if (response.credId > 0) {
                params.setLoginStateForMFA!(response.credId);
            }
            break;
        case LoginPageRedirect.RequireEmail:
            if (response.requireEmailCredId) {
                // Mark credential upgrade flow as active BEFORE components mount
                // This prevents RedirectForCredentialUpgrade from logging out during initial login
                markCredentialModalRendered();

                params.setLoginStateForRequireEmail!(
                    response.requireEmailCredId,
                    response.credUpgradeEmailSuggestion,
                );
            }
            break;
        case LoginPageRedirect.DataPrivacy:
            window.location.assign(routesWebforms.Misc.GetDataPrivacyUrl(response.dataPrivacyJWT!));
            break;
        case LoginPageRedirect.SubscriptionManagement:
            handleSubscriptionManagementRedirect();
            break;
        case LoginPageRedirect.NotConnected:
            window.location.assign(`/app${routes.notConnected.getLink(true)}`);
            break;
        default:
            // If we don't redirect, we need to reset the loading spinner
            params.resetLoadingSpinner!();
            break;
    }
};

const handleFrozenSubOwnerRedirect = () => {
    const url = getBaseRoute() + routes.account.frozenExternal;
    window.location.assign(url);
};

const handleFrozenBuilderRedirect = (response: LoginResponse) => {
    window.location.assign(
        getBaseRoute() +
            routes.account.getFrozenLink(
                response.loginStatus === LoginStatus.ClosedBuilder,
                response.isUserAdmin,
                response.builderName,
                response.encryptedRewinBuilderId,
                response.contactSupport,
            ),
    );
};

const handlePostLoginRedirect = (params: IRedirectPageParams, response: LoginResponse) => {
    const redirectUrl = getLastPageRequested(response.loginType, params.loginPostRedirect!);
    window.location.assign(redirectUrl);
};

const handleSubscriptionManagementRedirect = () => {
    let redirectRoute = `app/${routes.subscriptionManager.getSettingsLink(false, false)}`;
    redirectRoute = redirectRoute.replace("//", "/");
    window.location.assign(redirectRoute);
};

const getRedirectPage = (
    response: ILoginResponse,
    isOAuthRequest?: boolean,
    loginPostRedirect?: string,
): LoginPageRedirect => {
    const portalType = getPortalForLoginType(response.loginType);
    if (isOAuthRequest && response.oAuthCode) {
        return LoginPageRedirect.OAuth;
    }

    if (response instanceof LoginResponse) {
        if (!isNullOrUndefined(response.dataPrivacyJWT) && loginPostRedirect) {
            if (response.dataPrivacyJWT) {
                return LoginPageRedirect.DataPrivacy;
            } else {
                return LoginPageRedirect.NoRedirect;
            }
        }

        if (
            (portalType === PortalType.SUBS || portalType === PortalType.OWNER) &&
            (response.loginStatus === LoginStatus.ClosedBuilder ||
                response.loginStatus === LoginStatus.FrozenBuilder)
        ) {
            return LoginPageRedirect.FrozenSubOwner;
        }

        if (
            portalType === PortalType.BUILDER &&
            (response.loginStatus === LoginStatus.ClosedBuilder ||
                response.loginStatus === LoginStatus.FrozenBuilder)
        ) {
            return LoginPageRedirect.FrozenBuilder;
        }

        if (
            portalType === PortalType.BUILDER &&
            response.loginStatus === LoginStatus.NonBtOfferingBuilder
        ) {
            return LoginPageRedirect.SubscriptionManagement;
        }

        if (response.loginStatus === LoginStatus.RequiresMFA) {
            return LoginPageRedirect.MFA;
        }
        if (response.loginStatus === LoginStatus.RequireEmail) {
            return LoginPageRedirect.RequireEmail;
        }

        if (loginPostRedirect) {
            if (
                portalType === PortalType.SUBS &&
                response.loginStatus === LoginStatus.SuggestPasswordReset
            ) {
                return LoginPageRedirect.Summary;
            }
            if (response.loginStatus !== LoginStatus.Failed) {
                return LoginPageRedirect.PostLoginRedirect;
            } else {
                return LoginPageRedirect.NoRedirect;
            }
        }

        if (response.loginStatus === LoginStatus.NotConnected) {
            return LoginPageRedirect.NotConnected;
        }

        if (response.isSubNoJobAccess && response.subBidCount > 0) {
            return LoginPageRedirect.SubNoJobAccess;
        }

        if (
            (portalType === PortalType.BUILDER ||
                portalType === PortalType.SUBS ||
                portalType === PortalType.OWNER) &&
            response.loginStatus !== LoginStatus.Failed
        ) {
            return LoginPageRedirect.Summary;
        }
    }

    return LoginPageRedirect.NoRedirect;
};
